/**
 * Client-side RAG retriever — Gibbs `gibbs-client-rag` study.
 *
 * Drops Conifer's dependency on Dan's `/services/agents/retrieve/` for
 * the medchem corpus. Bundles a TF-IDF index over the distillates
 * corpus (built at compile time by `proto/scripts/buildRagCorpus.js`)
 * and answers `retrieveDocs(query, tier) → chunks[]` queries entirely
 * client-side, in <10 ms per query.
 *
 * **Algorithm**: classic TF-IDF over the unioned vocabulary of all
 * chunks. Cosine similarity between the query's TF-IDF vector and
 * each chunk's vector ranks the top-k. Stopword filter + a medchem-
 * aware tokenizer that preserves chemistry-specific tokens (-CF3,
 * α-hydrogen, σ-hole) as single units rather than splitting on
 * punctuation.
 *
 * **Stopword philosophy**: keep the list small. False stopwords hurt
 * retrieval quality more than including a few common words. The list
 * below is just English function-words plus the most boilerplate
 * markdown tokens.
 *
 * **Index laziness**: the TF-IDF index is built on first query rather
 * than at module load. Saves ~1 ms of work for sessions that never
 * trigger a retrieval. The corpus itself loads at module level since
 * it's a JSON import; only the index is deferred.
 *
 * @module gibbs/knowledge/clientRagRetriever
 */

import bundledCorpus from './bundledCorpus.json';

const STOPWORDS = new Set([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has',
    'have', 'how', 'in', 'is', 'it', 'its', 'of', 'on', 'or', 'that', 'the',
    'this', 'to', 'was', 'were', 'what', 'when', 'where', 'which', 'who',
    'why', 'with', 'i', 'you', 'we', 'they', 'them', 'will', 'would', 'can',
    'could', 'should', 'do', 'does', 'did', 'not', 'but', 'so', 'if',
    'then', 'than', 'into', 'about', 'over', 'under', 'between', 'some',
    'any', 'all', 'each', 'every', 'one', 'two', 'three',
]);

let cachedIndex = null;

/**
 * Tokenize a query / chunk into searchable tokens. Medchem-aware:
 * preserves chemistry-specific tokens that contain punctuation
 * (e.g., `-CF3`, `α-hydrogen`, `σ-hole`, `2-aminothiazole`,
 * `H-bond`) by NOT splitting on hyphens when the surrounding
 * characters look chemistry-shaped.
 *
 * Surfaced during the 2026-05-14 Bill Kinney walkthrough: queries
 * like "tune the H-bond" or "what about the α-hydrogen" got split
 * naively by general-purpose tokenizers and lost the chemistry
 * signal. Same fix the medchemRules `findRelevantRules` tokenizer
 * uses (`q.replace(/[^\w\s-]/g, ' ')`).
 *
 * @param {string} text
 * @returns {string[]} tokens (lowercased, stopword-filtered)
 */
export function tokenize(text) {
    if (!text || typeof text !== 'string') return [];
    return text
        .toLowerCase()
        // Strip non-word / non-space / non-hyphen characters. Keep
        // hyphens so chemistry compounds (h-bond, α-hydrogen) stay
        // as one token, but split on every other punctuation.
        .replace(/[^\w\s-]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length >= 2 && !STOPWORDS.has(t));
}

/**
 * Build the TF-IDF index over the bundled corpus. Idempotent and cached.
 *
 * @returns {{
 *   chunks: Array<object>,
 *   docVectors: Array<Map<string, number>>,
 *   idf: Map<string, number>,
 * }}
 */
function buildIndex() {
    if (cachedIndex) return cachedIndex;

    const chunks = bundledCorpus.chunks || [];
    const docFreq = new Map();
    const docTfs = chunks.map((chunk) => {
        const tokens = tokenize(`${chunk.heading} ${chunk.text}`);
        const tf = new Map();
        for (const t of tokens) tf.set(t, (tf.get(t) || 0) + 1);
        const seen = new Set();
        for (const t of tokens) {
            if (seen.has(t)) continue;
            seen.add(t);
            docFreq.set(t, (docFreq.get(t) || 0) + 1);
        }
        return tf;
    });

    const N = chunks.length || 1;
    const idf = new Map();
    for (const [term, df] of docFreq.entries()) {
        idf.set(term, Math.log((N + 1) / (df + 1)) + 1);
    }

    const docVectors = docTfs.map((tf) => {
        const vec = new Map();
        for (const [term, count] of tf.entries()) {
            const w = count * (idf.get(term) || 0);
            if (w > 0) vec.set(term, w);
        }
        return vec;
    });

    cachedIndex = { chunks, docVectors, idf };
    return cachedIndex;
}

function cosineSimilarity(a, b) {
    let dot = 0;
    let magA = 0;
    let magB = 0;
    for (const [, w] of a) magA += w * w;
    for (const [, w] of b) magB += w * w;
    if (magA === 0 || magB === 0) return 0;
    // Iterate over the smaller vector for the dot product
    const [smaller, larger] = a.size <= b.size ? [a, b] : [b, a];
    for (const [term, w] of smaller) {
        const o = larger.get(term);
        if (o) dot += w * o;
    }
    return dot / (Math.sqrt(magA) * Math.sqrt(magB));
}

/**
 * Run a TF-IDF retrieval against the bundled corpus.
 *
 * @param {string} query
 * @param {{ k?: number, tagBoosts?: Object<string, number> }} [opts]
 * @returns {Array<{ text: string, source: string, score: number,
 *   heading?: string, tags?: string[] }>}
 */
export function clientRetrieve(query, opts = {}) {
    const k = opts.k ?? 3;
    if (!query || typeof query !== 'string') return [];

    const index = buildIndex();
    if (!index.chunks || index.chunks.length === 0) return [];

    const queryTokens = tokenize(query);
    if (queryTokens.length === 0) return [];

    const queryTf = new Map();
    for (const t of queryTokens) queryTf.set(t, (queryTf.get(t) || 0) + 1);
    const queryVec = new Map();
    for (const [term, count] of queryTf.entries()) {
        const w = count * (index.idf.get(term) || 0);
        if (w > 0) queryVec.set(term, w);
    }

    const ranked = index.chunks.map((chunk, i) => {
        let score = cosineSimilarity(queryVec, index.docVectors[i]);

        // Optional tag-boost. Caller passes
        // { tagBoosts: { 'aniline': 0.1, 'toxicophore': 0.05 } } and any
        // chunk carrying a boosted tag has the boost added to its score.
        // Use sparingly — tag-boosting hides ranker quality issues.
        if (opts.tagBoosts && Array.isArray(chunk.tags)) {
            for (const tag of chunk.tags) {
                if (opts.tagBoosts[tag]) score += opts.tagBoosts[tag];
            }
        }

        return { chunk, score };
    });

    return ranked
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, k)
        .map((r) => ({
            text: r.chunk.text,
            source: r.chunk.source,
            heading: r.chunk.heading,
            tags: r.chunk.tags,
            score: r.score,
        }));
}

/**
 * Corpus metadata, for the stale-corpus warning the host emits when
 * the bundled corpus is older than 90 days.
 *
 * @returns {{ builtAt: string|null, chunkCount: number }}
 */
export function getCorpusMeta() {
    return {
        builtAt: bundledCorpus.meta?.builtAt || null,
        chunkCount: bundledCorpus.meta?.chunkCount
            || (bundledCorpus.chunks?.length || 0),
    };
}

/**
 * Reset cached state. Tests only.
 */
export function resetForTest() {
    cachedIndex = null;
}
